import { useState } from 'react';
import { NJButton, NJHeading } from '@engie-group/fluid-design-system-react';
import { RightSideModal } from './RightSideModalFixed';

interface PumpCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: PumpFormData) => void;
    plantId: string;
}

interface PumpFormData {
    name: string;
    type: 'primary' | 'secondary' | 'booster';
    flowRate: number;
    pressure: number;
    powerConsumption: number;
    group: string;
}

const INPUT_STYLE: React.CSSProperties = {
    width: '100%', minHeight: 40, padding: '10px 12px',
    border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
    borderRadius: 4, fontSize: 14, boxSizing: 'border-box',
};

const LABEL_STYLE: React.CSSProperties = {
    display: 'block', fontSize: 14, marginBottom: 6,
};

export function PumpCreateModal({ isOpen, onClose, onSubmit, plantId: _plantId }: PumpCreateModalProps) {
    const [formData, setFormData] = useState<PumpFormData>({
        name: '',
        type: 'primary',
        flowRate: 100,
        pressure: 2.5,
        powerConsumption: 15,
        group: 'groupe-1',
    });

    const handleSubmit = () => {
        if (!formData.name) {
            alert('Veuillez remplir tous les champs obligatoires');
            return;
        }
        onSubmit(formData);
        setFormData({ name: '', type: 'primary', flowRate: 100, pressure: 2.5, powerConsumption: 15, group: 'groupe-1' });
    };

    return (
        <RightSideModal
            isOpen={isOpen}
            onClose={onClose}
            title="Créer une pompe"
            metadata={<>Créé par PROTOTYPER le {new Date().toLocaleDateString('fr-FR')}</>}
            footer={
                <>
                    <NJButton label="Annuler" emphasis="subtle" onClick={onClose} />
                    <NJButton label="Créer" emphasis="bold" onClick={handleSubmit} />
                </>
            }
        >
            {/* Section: Informations générales */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Informations générales
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Nom *</label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Pompe 1"
                        style={INPUT_STYLE}
                    />
                    <span style={{ fontSize: 12, color: 'var(--nj-semantic-color-text-neutral-tertiary-default)', marginTop: 4, display: 'block' }}>
                        {formData.name.length}/64
                    </span>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Type *</label>
                    <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as PumpFormData['type'] })}
                        style={INPUT_STYLE}
                    >
                        <option value="primary">Primaire</option>
                        <option value="secondary">Secondaire</option>
                        <option value="booster">Surpresseur</option>
                    </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Groupe</label>
                    <select
                        value={formData.group}
                        onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                        style={INPUT_STYLE}
                    >
                        <option value="groupe-1">Groupe 1</option>
                        <option value="groupe-2">Groupe 2</option>
                        <option value="groupe-3">Groupe 3</option>
                    </select>
                </div>
            </div>

            {/* Section: Paramètres techniques */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Paramètres techniques
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Débit nominal (m³/h) *</label>
                    <input
                        type="number"
                        step="1"
                        value={formData.flowRate}
                        onChange={(e) => setFormData({ ...formData, flowRate: Number(e.target.value) })}
                        placeholder="100"
                        style={INPUT_STYLE}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Pression nominale (bar) *</label>
                    <input
                        type="number"
                        step="0.1"
                        value={formData.pressure}
                        onChange={(e) => setFormData({ ...formData, pressure: Number(e.target.value) })}
                        placeholder="2.5"
                        style={INPUT_STYLE}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Puissance électrique (kW) *</label>
                    <input
                        type="number"
                        step="0.1"
                        value={formData.powerConsumption}
                        onChange={(e) => setFormData({ ...formData, powerConsumption: Number(e.target.value) })}
                        placeholder="15"
                        style={INPUT_STYLE}
                    />
                </div>
            </div>
        </RightSideModal>
    );
}
